import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Truck, Package, MapPin, CheckCircle2, Circle, Clock, ArrowRight } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_ORDER = [
  { id: "shipment_created", label: "Order Confirmed" },
  { id: "picked_up",        label: "Picked Up" },
  { id: "in_transit",       label: "In Transit" },
  { id: "out_for_delivery", label: "Out for Delivery" },
  { id: "delivered",        label: "Delivered" },
];

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format((paise || 0) / 100);

const TrackOrderPage = () => {
  usePageTitle("Track your order");
  const [params, setParams] = useSearchParams();
  const [awb, setAwb] = useState(params.get("awb") || "");
  const [orderId, setOrderId] = useState(params.get("order_id") || "");
  const [identifier, setIdentifier] = useState(params.get("identifier") || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async (opts) => {
    const q = new URLSearchParams();
    if (opts?.awb || awb) q.set("awb", opts?.awb || awb);
    else if (orderId) { q.set("order_id", orderId); if (identifier) q.set("identifier", identifier); }
    else { setErr("Enter your AWB, or Order ID + registered phone/email."); return; }

    setBusy(true); setErr("");
    try {
      const r = await axios.get(`${API}/track-order?${q.toString()}`);
      setData(r.data);
      setParams(q, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not find that order.");
      setData(null);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (params.get("awb") || (params.get("order_id") && params.get("identifier"))) load({});
    // eslint-disable-next-line
  }, []);

  const status = data?.shipment?.status || (data?.fulfilment_status === "delivered" ? "delivered" : "shipment_created");
  const reachedIdx = STEP_ORDER.findIndex((s) => s.id === status);

  return (
    <section className="relative pt-16 pb-24 overflow-hidden min-h-[70vh]">
      <BotanicalBackdrop variant="corners" />
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-6 h-6" />
        <div className="label-eyebrow mt-3 text-center">Order Tracking</div>
        <h1 className="serif mt-4 text-center text-[42px] sm:text-[54px] leading-[1.05] text-[var(--hk-ink)]" data-testid="track-order-title">
          Where's your <span className="italic-accent text-[var(--hk-orange-deep)]">HerbKranti?</span>
        </h1>

        <div className="mt-10 hk-card p-6 lg:p-8 space-y-4" data-testid="track-order-form">
          <p className="text-[12px] text-[var(--hk-ink-2)] tracking-widest uppercase">Enter your AWB, or Order ID + registered contact</p>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              value={awb}
              onChange={(e) => setAwb(e.target.value)}
              placeholder="AWB Number (e.g. HK4C2A98F1)"
              className="h-11 px-3 rounded-md border border-[var(--hk-border)] bg-white text-[14px]"
              data-testid="track-awb"
            />
            <div className="md:col-span-1 grid grid-cols-2 gap-2">
              <input
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Order ID"
                className="h-11 px-3 rounded-md border border-[var(--hk-border)] bg-white text-[13px]"
                data-testid="track-order-id"
              />
              <input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Email / Phone"
                className="h-11 px-3 rounded-md border border-[var(--hk-border)] bg-white text-[13px]"
                data-testid="track-identifier"
              />
            </div>
          </div>
          <button onClick={() => load({})} disabled={busy} className="hk-btn-primary h-11 px-6" data-testid="track-submit">
            <Search size={14} /> {busy ? "Searching…" : "Track Order"}
          </button>
          {err && <p className="text-[13px] text-red-700">{err}</p>}
        </div>

        {data && (
          <div className="mt-8 space-y-6" data-testid="track-order-result">
            <div className="hk-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="label-eyebrow">Order</div>
                  <p className="serif text-[22px] mt-1">#{(data.order_id || "").slice(0, 8)}</p>
                  <p className="text-[13px] text-[var(--hk-ink-2)]">{data.customer_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-[var(--hk-ink-2)] uppercase tracking-widest">Amount</p>
                  <p className="serif text-[22px]">{rupees(data.total)}</p>
                  <p className="text-[11px] text-emerald-700 uppercase tracking-widest mt-1">{data.payment_status === "paid" ? "Paid" : data.payment_status}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[var(--hk-border)]">
                <p className="text-[12px] text-[var(--hk-ink-2)]">Items</p>
                <ul className="mt-1 text-[13.5px] text-[var(--hk-ink)]">
                  {(data.items || []).map((it, i) => (
                    <li key={i}>{it.name} × {it.qty}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="hk-card p-6">
              <div className="flex items-center gap-2">
                <Truck size={16} className="text-[var(--hk-orange-deep)]" />
                <p className="hk-h5">Shipment status</p>
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full border border-[var(--hk-border)] uppercase tracking-widest text-[var(--hk-ink-2)]">
                  {data.shipment?.courier || "Awaiting dispatch"}
                </span>
              </div>
              {data.shipment?.awb ? (
                <p className="mt-1 text-[12px] text-[var(--hk-ink-2)] font-mono">AWB {data.shipment.awb}</p>
              ) : (
                <p className="mt-1 text-[13px] text-[var(--hk-ink-2)]">Your order is confirmed. We'll email you the AWB the moment it dispatches.</p>
              )}

              <div className="mt-6 grid grid-cols-5 gap-2">
                {STEP_ORDER.map((s, i) => {
                  const done = i <= reachedIdx;
                  return (
                    <div key={s.id} className="flex flex-col items-center text-center gap-1.5" data-testid={`track-step-${s.id}`}>
                      {done ? <CheckCircle2 size={20} className="text-emerald-600" /> : <Circle size={20} className="text-[var(--hk-ink-3)]" />}
                      <span className={"text-[11px] leading-tight " + (done ? "text-[var(--hk-ink)] font-medium" : "text-[var(--hk-ink-3)]")}>{s.label}</span>
                    </div>
                  );
                })}
              </div>

              {data.shipment?.history?.length > 0 && (
                <div className="mt-6 pt-6 border-t border-[var(--hk-border)] space-y-2.5">
                  {data.shipment.history.slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-start gap-3 text-[13px]">
                      <Clock size={13} className="text-[var(--hk-ink-3)] mt-0.5" />
                      <div>
                        <p className="text-[var(--hk-ink)]"><b>{(h.status || "").replace(/_/g, " ")}</b> {h.note ? `— ${h.note}` : ""}</p>
                        <p className="text-[11px] text-[var(--hk-ink-3)]">{h.at ? new Date(h.at).toLocaleString("en-IN") : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="text-center">
              <Link to="/" className="text-[13px] text-[var(--hk-gold-deep)] hover:underline inline-flex items-center gap-1">
                Continue shopping <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TrackOrderPage;
